import { JtskCoordinates, Wgs84Coordinates } from '../../../models'; // Assuming your models are here

/**
 * Converts S-JTSK (EPSG:5514) coordinates to WGS84 (EPSG:4326) coordinates.
 *
 * @param jtskCoords The JTSK coordinates { x, y }.
 * @returns The WGS84 coordinates { lat, lon }.
 */
export function convertJtskToWgs84(
  jtskCoords: JtskCoordinates
): Wgs84Coordinates {
  if (
    typeof jtskCoords.x !== 'number' ||
    typeof jtskCoords.y !== 'number' ||
    isNaN(jtskCoords.x) ||
    isNaN(jtskCoords.y)
  ) {
    console.error(
      'Invalid JTSK coordinates provided for conversion:',
      jtskCoords
    );
    return { lat: 0, lon: 0 };
  }

  const wgs84_2 = convertJtskToWgs84CustomScript(jtskCoords.x, jtskCoords.y);
  return { lon: wgs84_2.lon, lat: wgs84_2.lat };
}

////////// CUSTOM PROJECTION SCRIPT /////////////

// The Proj4 library was returning incorrect results.
//  After unsuccessful debugging attempts, I decided  to use a custom script,
// inspired by https://martin.hinner.info/geo/ and refactored with Gemini AI.

// Bessel 1841 ellipsoid parameters (for S-JTSK)
const BESSEL_SEMI_MAJOR_AXIS = 6377397.15508;
const BESSEL_ECCENTRICITY_SQUARED = 0.006674372230614; // e^2 = (a^2 - b^2) / a^2; e = 0.081696831215303
const BESSEL_ECCENTRICITY = Math.sqrt(BESSEL_ECCENTRICITY_SQUARED); // Approx 0.081696831215303

// Krovak projection parameters
const KROVAK_N_PARAMETER = 0.97992470462083;
const KROVAK_CONST_U_RHO = 12310230.12797036;
const KROVAK_SIN_UQ = 0.863499969506341; // sin(Latitude of conformal sphere pole U_Q)
const KROVAK_COS_UQ = 0.504348889819882; // cos(Latitude of conformal sphere pole U_Q)
const KROVAK_SIN_VQ = 0.420215144586493; // sin(Longitude of conformal sphere pole V_Q)
const KROVAK_COS_VQ = 0.907424504992097; // cos(Longitude of conformal sphere pole V_Q)
const KROVAK_ALPHA_PARAMETER = 1.000597498371542; // Azimuth rotation
const KROVAK_K_PARAMETER = 1.003419163966575; // Scaling factor

// Helmert transformation parameters (S-JTSK to WGS84) - values from the original script
const HELMERT_DX = 570.69; // Translation in X (meters)
const HELMERT_DY = 85.69; // Translation in Y (meters)
const HELMERT_DZ = 462.84; // Translation in Z (meters)
const HELMERT_RX_SECONDS = -4.99821; // Rotation around X (arc-seconds)
const HELMERT_RY_SECONDS = -1.58676; // Rotation around Y (arc-seconds)
const HELMERT_RZ_SECONDS = -5.2611; // Rotation around Z (arc-seconds)
const HELMERT_SCALE_FACTOR_PPM_DIFF = 3.543e-6; // Scale factor difference (m = s - 1)

const ARC_SECONDS_TO_RADIANS = Math.PI / (180 * 3600);
const HELMERT_RX_RAD = HELMERT_RX_SECONDS * ARC_SECONDS_TO_RADIANS;
const HELMERT_RY_RAD = HELMERT_RY_SECONDS * ARC_SECONDS_TO_RADIANS;
const HELMERT_RZ_RAD = HELMERT_RZ_SECONDS * ARC_SECONDS_TO_RADIANS;

// WGS84 ellipsoid parameters
const WGS84_SEMI_MAJOR_AXIS = 6378137.0;
const WGS84_INVERSE_FLATTENING = 298.257223563;
const WGS84_FLATTENING = 1 / WGS84_INVERSE_FLATTENING;
const WGS84_ECCENTRICITY_SQUARED =
  2 * WGS84_FLATTENING - WGS84_FLATTENING * WGS84_FLATTENING;

/**
 * Performs a detailed mathematical transformation from S-JTSK (EPSG:5514) coordinates
 * to WGS84 (EPSG:4326) coordinates.
 * Inspired by https://martin.hinner.info/geo/
 *
 * @param jtskX JTSK X coordinate (Easting, or Krovak X depending on interpretation of source script)
 * @param jtskY JTSK Y coordinate (Northing, or Krovak Y depending on interpretation of source script)
 * @param jtskHeight Optional height in S-JTSK system (meters). Defaults to 0.
 * @returns WGS84 coordinates { lat, lon, height }
 */
function convertJtskToWgs84CustomScript(
  jtskX: number,
  jtskY: number,
  jtskHeight = 0
) {
  // --- Step 1: Calculate geographic coordinates (lat, lon on Bessel) from planar Krovak coordinates (Inverse Krovak Projection) ---
  const rhoKrovakPlane = Math.sqrt(jtskX * jtskX + jtskY * jtskY);
  const epsilonAngle = 2 * Math.atan(jtskY / (rhoKrovakPlane + jtskX)); // Polar angle on Krovak plane
  const capitalD = epsilonAngle / KROVAK_N_PARAMETER;

  const capitalS =
    2 *
      Math.atan(
        Math.exp(
          (1 / KROVAK_N_PARAMETER) *
            Math.log(KROVAK_CONST_U_RHO / rhoKrovakPlane)
        )
      ) -
    Math.PI / 2;

  const sinCapitalS = Math.sin(capitalS);
  const cosCapitalS = Math.cos(capitalS);

  const sinCapitalU =
    KROVAK_SIN_UQ * sinCapitalS -
    KROVAK_COS_UQ * cosCapitalS * Math.cos(capitalD);
  const cosCapitalU = Math.sqrt(1 - sinCapitalU * sinCapitalU);

  const sinDeltaV = (Math.sin(capitalD) * cosCapitalS) / cosCapitalU;
  const cosDeltaV = Math.sqrt(1 - sinDeltaV * sinDeltaV); // Sign ambiguity might need care depending on quadrant

  const sinCapitalV = KROVAK_SIN_VQ * cosDeltaV - KROVAK_COS_VQ * sinDeltaV;
  const cosCapitalV = KROVAK_COS_VQ * cosDeltaV + KROVAK_SIN_VQ * sinDeltaV;

  const longitudeBessel_rad =
    (2 * Math.atan(sinCapitalV / (1 + cosCapitalV))) / KROVAK_ALPHA_PARAMETER; // Lambda_JTSK

  // Iterative calculation for sin(Beta_JTSK)
  const tempT = Math.exp(
    (2 / KROVAK_ALPHA_PARAMETER) *
      Math.log((1 + sinCapitalU) / cosCapitalU / KROVAK_K_PARAMETER)
  );
  let iterativeSinBeta = (tempT - 1) / (tempT + 1); // Initial guess for sin(Beta_JTSK)

  let previousSinBetaForIteration;
  const ITERATION_TOLERANCE = 1e-15;
  const MAX_ITERATIONS = 100;
  let iterationCount = 0;

  do {
    previousSinBetaForIteration = iterativeSinBeta;
    const termLogNumerator =
      1 + BESSEL_ECCENTRICITY * previousSinBetaForIteration;
    const termLogDenominator =
      1 - BESSEL_ECCENTRICITY * previousSinBetaForIteration;

    if (termLogNumerator <= 0 || termLogDenominator <= 0) {
      console.error(
        'JTSK to WGS84: Invalid argument for Math.log in latitude iteration.'
      );
      // Fallback or throw error
      return { lat: NaN, lon: NaN, height: NaN };
    }

    const exponent =
      BESSEL_ECCENTRICITY * Math.log(termLogNumerator / termLogDenominator);
    const tempPom = tempT * Math.exp(exponent);
    iterativeSinBeta = (tempPom - 1) / (tempPom + 1);

    iterationCount++;
    if (iterationCount > MAX_ITERATIONS) {
      console.warn(
        'JTSK to WGS84: Max iterations reached in latitude calculation.'
      );
      break;
    }
  } while (
    Math.abs(iterativeSinBeta - previousSinBetaForIteration) >
    ITERATION_TOLERANCE
  );

  const latitudeBessel_rad = Math.atan(
    iterativeSinBeta / Math.sqrt(1 - iterativeSinBeta * iterativeSinBeta)
  ); // Beta_JTSK

  // --- Step 2: Convert geographic Bessel coordinates to Cartesian Bessel coordinates (X, Y, Z) ---
  const nuBessel =
    BESSEL_SEMI_MAJOR_AXIS /
    Math.sqrt(
      1 -
        BESSEL_ECCENTRICITY_SQUARED *
          Math.sin(latitudeBessel_rad) *
          Math.sin(latitudeBessel_rad)
    ); // Radius of curvature in prime vertical

  const cartesianX_Bessel =
    (nuBessel + jtskHeight) *
    Math.cos(latitudeBessel_rad) *
    Math.cos(longitudeBessel_rad);
  const cartesianY_Bessel =
    (nuBessel + jtskHeight) *
    Math.cos(latitudeBessel_rad) *
    Math.sin(longitudeBessel_rad);
  const cartesianZ_Bessel =
    ((1 - BESSEL_ECCENTRICITY_SQUARED) * nuBessel + jtskHeight) *
    Math.sin(latitudeBessel_rad);

  // --- Step 3: Apply 7-parameter Helmert transformation (Bessel Cartesian to WGS84 Cartesian) ---
  // Using the position vector convention: P_wgs84 = T + (1+s) * R * P_bessel
  const scale = 1 + HELMERT_SCALE_FACTOR_PPM_DIFF;

  const cartesianX_Wgs84 =
    HELMERT_DX +
    scale *
      (cartesianX_Bessel +
        HELMERT_RZ_RAD * cartesianY_Bessel -
        HELMERT_RY_RAD * cartesianZ_Bessel);
  const cartesianY_Wgs84 =
    HELMERT_DY +
    scale *
      (-HELMERT_RZ_RAD * cartesianX_Bessel +
        cartesianY_Bessel +
        HELMERT_RX_RAD * cartesianZ_Bessel);
  const cartesianZ_Wgs84 =
    HELMERT_DZ +
    scale *
      (HELMERT_RY_RAD * cartesianX_Bessel -
        HELMERT_RX_RAD * cartesianY_Bessel +
        cartesianZ_Bessel);

  // --- Step 4: Convert Cartesian WGS84 coordinates to geographic WGS84 coordinates (lat, lon, height) ---
  // Iterative method (e.g., Bowring or similar)
  const pWgs84 = Math.sqrt(
    cartesianX_Wgs84 * cartesianX_Wgs84 + cartesianY_Wgs84 * cartesianY_Wgs84
  );

  // Initial estimate for latitude
  let latitudeWgs84_rad = Math.atan(
    cartesianZ_Wgs84 / (pWgs84 * (1 - WGS84_ECCENTRICITY_SQUARED))
  );
  const longitudeWgs84_rad = Math.atan2(cartesianY_Wgs84, cartesianX_Wgs84); // atan2 handles quadrants correctly

  let nuWgs84;
  let heightWgs84;
  let previousLatitudeWgs84_rad;
  iterationCount = 0; // Reset for this loop

  do {
    previousLatitudeWgs84_rad = latitudeWgs84_rad;
    nuWgs84 =
      WGS84_SEMI_MAJOR_AXIS /
      Math.sqrt(
        1 -
          WGS84_ECCENTRICITY_SQUARED *
            Math.sin(previousLatitudeWgs84_rad) *
            Math.sin(previousLatitudeWgs84_rad)
      );
    heightWgs84 = pWgs84 / Math.cos(previousLatitudeWgs84_rad) - nuWgs84;
    latitudeWgs84_rad = Math.atan(
      (cartesianZ_Wgs84 / pWgs84) *
        (1 /
          (1 -
            WGS84_ECCENTRICITY_SQUARED * (nuWgs84 / (nuWgs84 + heightWgs84))))
    );

    iterationCount++;
    if (iterationCount > MAX_ITERATIONS) {
      // Using same MAX_ITERATIONS for simplicity
      console.warn('WGS84 Cartesian to Geodetic: Max iterations reached.');
      break;
    }
  } while (
    Math.abs(latitudeWgs84_rad - previousLatitudeWgs84_rad) >
    ITERATION_TOLERANCE
  );

  // Final calculation for height with converged latitude
  nuWgs84 =
    WGS84_SEMI_MAJOR_AXIS /
    Math.sqrt(
      1 -
        WGS84_ECCENTRICITY_SQUARED *
          Math.sin(latitudeWgs84_rad) *
          Math.sin(latitudeWgs84_rad)
    );
  heightWgs84 = pWgs84 / Math.cos(latitudeWgs84_rad) - nuWgs84;

  // Convert radians to degrees
  const latWgs84_deg = latitudeWgs84_rad * (180 / Math.PI);
  const lonWgs84_deg = longitudeWgs84_rad * (180 / Math.PI);

  return {
    lat: latWgs84_deg,
    lon: lonWgs84_deg,
    height: heightWgs84,
  };
}
